import Vue from "vue";
import moment from "moment";
import { mapGetters } from "vuex";
import SpinnerMixin from "@/mixins/spinner";
import appointmentApi from "@/api/appointment/appointmentApiHttp";
import { dateValidationRule } from "@/helpers/formValidation";
import { addDays, getToday } from "@/helpers/dateTimeHelper";
import { downloadFile } from "@/helpers/fileHelper";
export default Vue.extend({
    mixins: [SpinnerMixin],
    data() {
        return {
            officesData: [],
            customersData: [],
            cancelTypeData: [
                { name: this.$t("calendar.cancelType.notCanceled"), id: 0 },
                { name: this.$t("calendar.cancelType.canceledWithoutCharge"), id: 1 },
                { name: this.$t("calendar.cancelType.canceledWithCharge"), id: 2 }
            ],
            isDataLoaded: false,
            officeId: null,
            // Last 7 days, today included
            startDate: addDays(getToday(), -6),
            endDate: getToday(),
            customerIds: [],
            // As in the payments page search, the appointments canceled without charge are excluded
            cancelTypes: [0, 2],
            paid: 0,
            validationOptions: {
                rules: {
                    exportStartDate: {
                        required: true,
                        date: [dateValidationRule]
                    },
                    exportEndDate: {
                        required: true,
                        date: [dateValidationRule],
                        endDateNotBeforeStartDate: [
                            (args) => {
                                const startDate = args.element.form.querySelector("#exportStartDate").ej2_instances[0].value;
                                const endDate = args.element.form.querySelector("#exportEndDate").ej2_instances[0].value;
                                return (startDate == null || endDate == null || endDate >= startDate);
                            },
                            this.$t("payments.export.endDateBeforeStartDateError")
                        ]
                    }
                }
            }
        };
    },
    computed: {
        ...mapGetters({
            hasMultipleOffices: "account/hasMultipleOffices"
        })
    },
    methods: {
        async exportPayments() {
            const request = {
                startDate: moment(this.startDate).format("YYYY-MM-DD"),
                endDate: moment(this.endDate).format("YYYY-MM-DD"),
                officeId: this.officeId ?? undefined,
                customerIds: this.customerIds,
                cancelTypes: this.cancelTypes,
                isPaid: this.paid === -1 ? undefined : this.paid === 1
            };
            // The dialog is closed right away and the page spinner shows the export in progress, so any
            // failure is notified with a toast instead of the form errors
            this.$parent.$parent.hide();
            try {
                const { file, fileName } = await appointmentApi.exportPayments(request);
                downloadFile(file, fileName);
            }
            catch (errors) {
                this.$toast.showWarningToast(this.$t("shared.toastFailureTitle"), errors[0].message);
            }
        },
        cancel() {
            this.$parent.$parent.hide();
        },
        applyFilters(filters) {
            this.officeId = filters.officeId;
            // The period falls back to the last 7 days when it is not set in the page header
            this.startDate = filters.startDate ?? addDays(getToday(), -6);
            this.endDate = filters.endDate ?? getToday();
            this.customerIds = [...filters.customerIds];
            this.cancelTypes = [...filters.cancelTypes];
            this.paid = filters.paid;
        },
        async onShow(filters) {
            this.applyFilters(filters);
            // On the first opening the offices and customers are loaded after the filters were set, so
            // the filters are set again to select them in the lists
            if (!this.isDataLoaded) {
                await this.loadData();
                this.applyFilters(filters);
            }
        },
        async loadData() {
            if (this.isDataLoaded)
                return;
            try {
                const offices = await this.$store.dispatch("office/getAll");
                const customers = await this.$store.dispatch("customer/getAll", {
                    load: true
                });
                // The store lists are copied, as they are shared with the search toolbar
                this.officesData = [
                    { id: null, name: this.$t("offices.all") },
                    ...offices.map((office) => ({
                        id: office.id,
                        name: office.name
                    }))
                ];
                this.customersData = customers
                    .filter(customer => customer.deletedAt == undefined)
                    .map(customer => ({
                    id: customer.id,
                    fullName: `${customer.name} ${customer.surname}`
                }));
                this.isDataLoaded = true;
            }
            catch (errors) {
                this.$toast.showDangerToast(this.$t("shared.toastFailureTitle"), this.$t("shared.toastFailureContent", {
                    error: errors[0].message
                }));
            }
        }
    },
    mounted() {
        // The content is created with the payments page, so the data is loaded when the dialog is first opened
        this.$parent.$parent.$on("show", this.onShow);
    },
    beforeDestroy() {
        this.$parent.$parent.$off("show", this.onShow);
    }
});
